/**
 * Formatting helpers. All times are stored/queried in UTC and rendered in the
 * user's local timezone here — the single place tz conversion happens.
 */
import { format, formatDistanceToNowStrict, parseISO } from 'date-fns';

// Indian grouping everywhere (1,00,000 / 12,34,567).
const numberFmt = new Intl.NumberFormat('en-IN');
const rupeeFmt = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const pctFmt = new Intl.NumberFormat('en-IN', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** Compact integer, e.g. 12,480. */
export function formatNumber(value: number): string {
  return numberFmt.format(Math.round(value));
}

/** A rupee amount in Indian format, e.g. 1234567 -> "₹12,34,567" or 5.5 -> "₹5.50". */
export function formatRupees(value: number): string {
  return rupeeFmt.format(value);
}

/** Compact large numbers, e.g. 1.2M, 12.4k. */
export function formatCompact(value: number): string {
  return new Intl.NumberFormat('en-IN', { notation: 'compact', maximumFractionDigits: 1 }).format(
    value,
  );
}

/** A 0..1 ratio as a percent string, e.g. 0.9824 -> "98.2%". */
export function formatPercent(ratio: number): string {
  return pctFmt.format(ratio);
}

/** A latency in ms, e.g. 142 -> "142 ms", 1840 -> "1.84 s". */
export function formatLatency(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

/** ISO/Date -> local clock time, e.g. "14:32:08". */
export function formatTime(iso: string | number | Date): string {
  return format(new Date(iso), 'HH:mm:ss');
}

/** ISO/Date -> local date+time, e.g. "Jun 3, 14:32". */
export function formatDateTime(iso: string | number | Date): string {
  return format(new Date(iso), 'MMM d, HH:mm');
}

/** Calendar day, e.g. "2026-10-05" -> "05 Oct 2026". Date-only strings are read as local days (no UTC shift). */
export function formatDate(value: string | Date): string {
  return format(typeof value === 'string' ? parseISO(value) : value, 'dd MMM yyyy');
}

/** Short weekday of a calendar day, e.g. "2026-10-05" -> "Mon". */
export function formatWeekday(value: string | Date): string {
  return format(typeof value === 'string' ? parseISO(value) : value, 'EEE');
}

/** Relative "x ago", e.g. "12s ago". Returns "—" for nullish. */
export function formatRelative(iso: string | number | Date | null | undefined): string {
  if (!iso) return '—';
  return `${formatDistanceToNowStrict(new Date(iso))} ago`;
}

/** Seconds of uptime -> "3d 04h 12m". */
export function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (d) parts.push(`${d}d`);
  parts.push(`${String(h).padStart(2, '0')}h`);
  parts.push(`${String(m).padStart(2, '0')}m`);
  return parts.join(' ');
}

/** The user's resolved IANA timezone, for display ("America/New_York"). */
export function localTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
